import {Injectable, Injector} from '@angular/core';
import {
    ApplicationCountyEligibilityResult,
    AssistanceApplication,
    AssistanceApplicationClient,
    AssistanceApplicationType,
    Company,
    DraftApplication,
    DraftApplicationModel, FileResponse,
    FundType,
    LookUp,
    LookUpCategoryType,
    PagedListOfAssistanceApplicationListPreview,
    PublicClient,
    PublicInternalFilter, User
} from '../../../services/api.client';
import {BehaviorSubject, firstValueFrom, Observable, of} from 'rxjs';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators as FormValidators} from '@angular/forms';
import {Validators} from '../../../utilities/Validators';
import {emailOrPhoneRequiredValidator} from './assistance-application/eligibility-screening-form/validators';

import {Race} from '../../../shared/enums/race';
import {Gender} from '../../../shared/enums/gender';
import {EmploymentStatus} from '../../../shared/enums/employment-status';
import {
    publicAssistanceApplicationRequiredDocumentsUploadedValidator,
    ssnValidator,
    stateValidator,
    stateValidatorForValidStates,
    zipCodeValidator
} from './validators';
import {AssistanceApplicationFileType} from '../../../shared/enums/assistance-application-file-type';
import {ActivatedRoute, Router} from '@angular/router';
import {NotificationService} from '../../../services/NotificationService';
import swal from 'sweetalert2';
import {distinctUntilChanged, share, takeUntil} from 'rxjs/operators';
import {AssistanceApplicationStatus} from '../../../shared/enums/assistance-application-status';
import {Disposable} from '../../../utilities/Disposable';
import {IncomeValidatorService} from '../../../shared/utils/income-validator.service';
import {AgreementPdfService} from "../../../services/agreement-pdf-service";
import {TranslateService} from "@ngx-translate/core";
import {
    EMPLOYMENT_STATUS_TRANSLATIONS,
    GENDER_TRANSLATIONS,
    RACE_TRANSLATIONS
} from "./assistance-application/standard-questions/translations";
import {environment} from '../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AssistanceApplicationService extends Disposable {
    public isValidUrlSubject = new BehaviorSubject<boolean | null>(null);
    public isValidUrl$ = this.isValidUrlSubject.asObservable();
    public programType$ = new BehaviorSubject<AssistanceApplicationType | null>(null);
    private _isBirthYearAndSsnOptional = false;

    public get isBirthYearAndSsnOptional(): boolean {
        return this._isBirthYearAndSsnOptional;
    }

    private raceOptionsSubject = new BehaviorSubject<LookUp[]>([]);
    private employmentStatusOptionsSubject = new BehaviorSubject<LookUp[]>([]);
    private genderOptionsSubject = new BehaviorSubject<LookUp[]>([]);
    public raceOptions: LookUp[] = [];
    public employmentStatusOptions: LookUp[] = [];
    public genderOptions: LookUp[] = [];
    public raceOptionsEnglish: LookUp[] = [];
    public employmentStatusOptionsEnglish: LookUp[] = [];
    public genderOptionsEnglish: LookUp[] = [];

    public assistanceOptions: LookUp[] = [];
    public unionInfoOptions: LookUp[] = [];
    public currentDraftId: string;
    public signedAgreementIsValid$ = new BehaviorSubject<boolean>(false);
    private includedRaces = [
        Race.AmericanIndian,
        Race.EuropeanAmerican,
        Race.AsianAmerican,
        Race.SouthAsian,
        Race.Jewish,
        Race.MiddleEastern,
        Race.PacificIslander,
        Race.Hispanic,
        Race.AfricanAmerican,
        Race.Biracial,
        Race.Other
    ];
    private includedGenders = [
        Gender.Male,
        Gender.Female,
        Gender.NotDisclose,
        Gender.NonBinary,
        Gender.SelfDescribe,
        Gender.Unknown
    ];
    private includedEmploymentStatuses = [
        EmploymentStatus.PartTimeEmployment,
        EmploymentStatus.FullTimeEmployment,
        EmploymentStatus.Retired,
        EmploymentStatus.SelfEmployed,
        EmploymentStatus.UnemployedWithBenefits,
        EmploymentStatus.UnemployedNoBenefits,
        EmploymentStatus.ActiveMilitary,
        EmploymentStatus.Student,
    ];
    private currentPledgeType: string;
    private isSavingDraftSubject = new BehaviorSubject<boolean>(false);
    public isSavingDraft$ = this.isSavingDraftSubject.asObservable();

    public form = this.fb.group(
        {
            eligibilityScreening: this.fb.group(
                {
                    state: new FormControl<string>('', {
                        validators: [Validators.required, stateValidator()]
                    }),
                    county: new FormControl<string>('', Validators.required),
                    childrenCount: new FormControl<number>(0, [Validators.required]),
                    adultCount: new FormControl<number>(1, [Validators.required]),
                    householdIncome: new FormControl<number>(0, {
                        validators: [Validators.required]
                    }),
                    contactPreference: new FormControl<string>('', Validators.required),
                    email: new FormControl<string>('', [Validators.email]),
                    phone: new FormControl<string>('', [
                        Validators.minLength(9, [/-/g, /_/g, /\(/g, /\)/g])
                    ]),
                    agreedToNotifications: new FormControl<boolean>(false, [FormValidators.requiredTrue]),
                },
                {
                    validators: [emailOrPhoneRequiredValidator()]
                }
            ),
            standardQuestions: this.fb.group({
                applicantInformation: this.fb.group({
                    id: [null],
                    accountNumber: [''],
                    totalAmountDue: this.fb.control<number | null>(null),
                    emailAddress: ['', Validators.email],
                    firstName: ['', Validators.required],
                    lastName: ['', Validators.required],
                    phone: ['', [Validators.minLength(9, [/-/g, /_/g, /\(/g, /\)/g])]],
                    birthYear: new FormControl(null, Validators.compose(Validators.birthYear({kind: 'adult'}))),
                    address: ['', Validators.required],
                    zipCode: [
                        '',
                        {validators: [Validators.required, zipCodeValidator()]}
                    ],
                    ssn: ['', [Validators.required, ssnValidator()]],
                    employmentStatus: ['', Validators.required],
                    race: [null],
                    otherRace: [''],
                    gender: [null],
                    totalMonthlyIncome: new FormControl<number>(0, [Validators.required]),
                    totalHouseholdMonthlyIncome: new FormControl<number | null>(null, {
                        validators: [Validators.required]
                    }),
                    alternativePhone: ['', [
                        Validators.minLength(9, [/-/g, /_/g, /\(/g, /\)/g])
                    ]],
                    county: ['', Validators.required],
                    state: new FormControl<string>('', {
                        validators: [Validators.required, stateValidatorForValidStates()]
                    }),
                    unionInfo: new FormControl('Not Applicable', Validators.required),
                    isElderly: new FormControl(false),
                    isDisabled: new FormControl(false),
                    hasChildrenUnderFive: new FormControl(false),
                    isVeteran: new FormControl(false),
                    isHomeless: new FormControl(false),
                    membersAreElderly: new FormControl(false),
                    membersAreDisabled: new FormControl(false),
                    membersHaveChildrenUnderFive: new FormControl(false)
                }),
                householdInformation: this.fb.group({
                    adults: this.fb.array([]),
                    minors: this.fb.array([])
                }),
                assistanceRequestDescription: this.fb.group({
                    uhonsRent: [false],
                    evictionNotification: [false],
                    uhonsElectric: [false],
                    uhonsGasHeat: [false],
                    uhonsWater: [false],
                    uhonsSewer: [false],
                    uhonsChildcareExpenses: [false],
                    uhonsTransportationRideShare: [false],
                    uhonsCarRepair: [false],
                    stlWaterWater: [false]
                }),
                circumstances: ['', Validators.maxLength(2048)]
            }),
            files: this.fb.array([]),
            agreementForm: this.fb.group({
                section1: this.fb.control(false, [FormValidators.requiredTrue]),
                section2: this.fb.control(false, [FormValidators.requiredTrue]),
                section3: this.fb.control(false, [FormValidators.requiredTrue]),
                section4: this.fb.control(false, [FormValidators.requiredTrue]),
                section5: this.fb.control(false, [FormValidators.requiredTrue]),
                section6: this.fb.control(false, [FormValidators.requiredTrue]),
                name: this.fb.control(''),
                address: this.fb.control(''),
                phone: this.fb.control(''),
                accountNumber: this.fb.control(''),
                date: this.fb.control(new Date())
            })
        },
        {
            validators: [publicAssistanceApplicationRequiredDocumentsUploadedValidator()]
        }
    );

    public signedAgreementFile: any;
    public signedAgreementIsAttached: boolean;

    public get employmentStatusOptions$(): Observable<LookUp[]> {
        return this.employmentStatusOptionsSubject.asObservable();
    }

    public get raceOptions$(): Observable<LookUp[]> {
        return this.raceOptionsSubject.asObservable();
    }

    public get genderOptions$(): Observable<LookUp[]> {
        return this.genderOptionsSubject.asObservable();
    }

    public get programType(): AssistanceApplicationType {
        return this.programType$.getValue();
    }

    get adults(): FormArray {
        return this.standardQuestions
            .get('householdInformation')
            .get('adults') as FormArray;
    }

    get minors(): FormArray {
        return this.standardQuestions
            .get('householdInformation')
            .get('minors') as FormArray;
    }

    get eligibilityScreeningFormGroup(): FormGroup {
        return this.form.get('eligibilityScreening') as FormGroup;
    }

    get standardQuestions(): FormGroup {
        return this.form.get('standardQuestions') as FormGroup;
    }

    get filesFormArray(): FormArray {
        return this.form.get('files') as FormArray;
    }

    get eligibilityState(): FormControl {
        return this.eligibilityScreeningFormGroup?.get('state') as FormControl;
    }

    get eligibilityCounty(): FormControl {
        return this.eligibilityScreeningFormGroup?.get('county') as FormControl;
    }

    get eligibilityHouseholdIncome(): FormControl {
        return this.eligibilityScreeningFormGroup?.get(
            'householdIncome'
        ) as FormControl;
    }

    get eligibilityAdultCount(): FormControl {
        return this.eligibilityScreeningFormGroup?.get('adultCount') as FormControl;
    }

    get eligibilityChildrenCount(): FormControl {
        return this.eligibilityScreeningFormGroup?.get(
            'childrenCount'
        ) as FormControl;
    }

    get standardQuestionsState(): FormControl {
        return this.standardQuestions
            ?.get('applicantInformation')
            ?.get('state') as FormControl;
    }

    get standardQuestionsCounty(): FormControl {
        return this.standardQuestions
            ?.get('applicantInformation')
            ?.get('county') as FormControl;
    }

    get standardQuestionsTotalHouseholdMonthlyIncome(): FormControl {
        return this.standardQuestions
            ?.get('applicantInformation')
            ?.get('totalHouseholdMonthlyIncome') as FormControl;
    }

    public get requestingUtilityAssistance(): boolean {
        const assistanceRequestDescription = this.standardQuestions.get(
            'assistanceRequestDescription'
        ) as FormGroup;
        return (
            assistanceRequestDescription.get('uhonsElectric').value ||
            assistanceRequestDescription.get('uhonsGasHeat').value ||
            assistanceRequestDescription.get('uhonsWater').value ||
            assistanceRequestDescription.get('uhonsSewer').value
        );
    }

    public get requestingChildcareAssistance(): boolean {
        const assistanceRequestDescription = this.standardQuestions.get(
            'assistanceRequestDescription'
        ) as FormGroup;
        return assistanceRequestDescription.get('uhonsChildcareExpenses').value;
    }

    public get requestingCarRepairAssistance(): boolean {
        const assistanceRequestDescription = this.standardQuestions.get(
            'assistanceRequestDescription'
        ) as FormGroup;
        return assistanceRequestDescription.get('uhonsCarRepair').value;
    }

    public get requestingRentAssistance(): boolean {
        const assistanceRequestDescription = this.standardQuestions.get(
            'assistanceRequestDescription'
        ) as FormGroup;
        return assistanceRequestDescription.get('uhonsRent').value;
    }

    public get requestingEvictionAssistance(): boolean {
        const assistanceRequestDescription = this.standardQuestions.get(
            'assistanceRequestDescription'
        ) as FormGroup;
        return assistanceRequestDescription.get('evictionNotification').value;
    }

    public get agreementFormGroup(): FormGroup {
        return this.form.get('agreementForm') as FormGroup;
    }

    constructor(
        private fb: FormBuilder,
        private _assistanceApplicationClient: AssistanceApplicationClient,
        private _notificationService: NotificationService,
        private publicClient: PublicClient,
        private router: Router,
        private route: ActivatedRoute,
        private injector: Injector,
        private incomeValidator: IncomeValidatorService,
        private pdf: AgreementPdfService,
        private translate: TranslateService
    ) {
        super();
    }

    public setProgramType(programType: AssistanceApplicationType): void {
        this.programType$.next(programType);
        this.fetchFundTypeConfiguration(programType);
        this.loadAssistanceOptions(programType);
    }

    private loadAssistanceOptions(programType: AssistanceApplicationType): void {
        let lookupCategory: LookUpCategoryType;
        switch (programType) {
            case AssistanceApplicationType.STLWater:
                lookupCategory = LookUpCategoryType.AdministrativeAgencyFundAssistanceSTLWater;
                break;
            case AssistanceApplicationType.UHONS:
            default:
                lookupCategory = LookUpCategoryType.AdministrativeAgencyFundAssistanceUhons;
                break;
        }
        this.publicClient.retrieveLookUps(lookupCategory).subscribe(x => {
            this.assistanceOptions = x.items;
        });
    }

    private fetchFundTypeConfiguration(programType: AssistanceApplicationType): void {
        this.publicClient.getFundTypeConfiguration(programType).subscribe({
            next: (fundType: FundType) => {
                this._isBirthYearAndSsnOptional = fundType?.isBirthYearAndSsnOptional ?? false;
            },
            error: () => {
                this._isBirthYearAndSsnOptional = false;
            }
        });
    }

    public setProgramIncomeValidator(): void {
        const group = this.eligibilityScreeningFormGroup;
        if (!group || this.programType === null || this.programType === undefined) {
            return;
        }
        group.addValidators(
            this.incomeValidator.eligibilityScreeningIncomeValidator(
                this.programType
            )
        );
        group.updateValueAndValidity({emitEvent: true});
    }

    public setIsValidUrl(pledgeType: string): void {
        this.publicClient.isValidUrl(pledgeType).subscribe({
         next: (isValidResult) => {
             this.isValidUrlSubject.next(isValidResult);
             if (isValidResult) {
                 this.currentPledgeType = pledgeType;
             }
         },
         error: () => {
             this.isValidUrlSubject.next(false);
         }
     });
    }

    public checkIsCountyActive(county: string): Observable<ApplicationCountyEligibilityResult> {
        return this.publicClient.isApplicationCountyActive(
            county,
            this.programType
        );
    }

    public addFileFormGroup(
        fileType?: AssistanceApplicationFileType | undefined
    ): void {
        const formGroup = this.fb.group({
            fileType: [fileType, Validators.required],
            file: [null, Validators.required]
        });
        const formArray = this.form.get('files') as FormArray;
        formArray.push(formGroup);
    }

    public addFile(fileType: AssistanceApplicationFileType, file: File): void {
        const formGroup = this.fb.group({
            fileType: [fileType, Validators.required],
            file: [file, Validators.required]
        });
        const formArray = this.form.get('files') as FormArray;
        formArray.push(formGroup);
    }

    public async generateAgreementPdf(
        downloadFile?: boolean
    ): Promise<Blob> {
        const leftSealBase64 = await this.pdf.imageToBase64(
            'assets/images/stl-water/stl_seal.png'
        );
        const rightSealBase64 = await this.pdf.imageToBase64(
            'assets/images/stl-water/cor_logo_1.png'
        );
        return await this.pdf.generateAgreementPdf(
            this.agreementFormGroup,
            leftSealBase64,
            rightSealBase64,
            downloadFile
        );
    }

    public async addAgreementPdfToApplicationFiles(): Promise<void> {
        if (this.signedAgreementIsAttached) {
            return;
        }
        const agreementBlob = await this.generateAgreementPdf();
        const pdfFile = new File(
            [agreementBlob],
            'Water_Utility_Assistance_Repayment_Agreement.pdf',
            {
                type: 'application/pdf',
                lastModified: new Date().getTime()
            }
        );
        this.addFile(
            AssistanceApplicationFileType.Other,
            pdfFile
        );
        this.signedAgreementIsAttached = true;
    }

    public async setDropdownOptions() {

        const empResponse = await firstValueFrom(this.publicClient.retrieveLookUps(LookUpCategoryType.EmploymentStatus));
        const includedEmploymentStatusesStrings = this.includedEmploymentStatuses.map(o => o.toString());
        this.employmentStatusOptionsSubject.next(empResponse.items.filter(item => includedEmploymentStatusesStrings.includes(item.key)));
        this.employmentStatusOptionsEnglish = [...this.employmentStatusOptionsSubject.value];

        const raceResponse = await firstValueFrom(this.publicClient.retrieveLookUps(LookUpCategoryType.Race));
        const includedRacesString = this.includedRaces.map(o => o.toString());
        this.raceOptionsSubject.next(raceResponse.items.filter(item => includedRacesString.includes(item.key)));
        this.raceOptionsEnglish = [...this.raceOptionsSubject.value];

        const genderResponse = await firstValueFrom(this.publicClient.retrieveLookUps(LookUpCategoryType.Gender));
        const includedGendersString = this.includedGenders.map(o => o.toString());
        this.genderOptionsSubject.next(genderResponse.items.filter(item => includedGendersString.includes(item.key)));
        this.genderOptionsEnglish = [...this.genderOptionsSubject.value];

        this.publicClient.retrieveLookUps(LookUpCategoryType.UnionInfo).subscribe(x => {
            this.unionInfoOptions = x.items.sort((a, b) => {
                return a.sortOrder < b.sortOrder ? -1 : 1;
            });
        });
    }

    public handleLanguageChange(): void {
        this.translate.onLangChange.pipe(
            takeUntil(this.$destroyed)
        ).subscribe(() => {
            this.setTranslatedDropdownOptions();
        });
    }

    public setTranslatedDropdownOptions(): void {

        if (this.translate.currentLang === 'en') {
            this.employmentStatusOptionsSubject.next([...this.employmentStatusOptionsEnglish]);
            this.raceOptionsSubject.next([...this.raceOptionsEnglish]);
            this.genderOptionsSubject.next([...this.genderOptionsEnglish]);
            return;
        }

        const empOptions =  [...this.employmentStatusOptionsEnglish];
        this.employmentStatusOptionsSubject.next(empOptions.map(item => (
            LookUp.fromJS(
                {
                    category: item.category,
                    key: item.key,
                    lookupValue: EMPLOYMENT_STATUS_TRANSLATIONS[item.lookupValue]
                        ? EMPLOYMENT_STATUS_TRANSLATIONS[item.lookupValue]
                        : item.lookupValue,
                    description: item.description,
                    sortOrder: item.sortOrder,
                    id: item.id,
                }
            )
        )));

        const raceSelectOptions =  [...this.raceOptionsEnglish];
        this.raceOptionsSubject.next(raceSelectOptions.map(item => (
            LookUp.fromJS(
                {
                    category: item.category,
                    key: item.key,
                    lookupValue: RACE_TRANSLATIONS[item.lookupValue]
                        ? RACE_TRANSLATIONS[item.lookupValue]
                        : item.lookupValue,
                    description: item.description,
                    sortOrder: item.sortOrder,
                    id: item.id,
                }
            )
        )));

        const genderSelectOptions =  [...this.genderOptionsEnglish];
        this.genderOptionsSubject.next(genderSelectOptions.map(item => (
            LookUp.fromJS(
                {
                    category: item.category,
                    key: item.key,
                    lookupValue: GENDER_TRANSLATIONS[item.lookupValue]
                        ? GENDER_TRANSLATIONS[item.lookupValue]
                        : item.lookupValue,
                    description: item.description,
                    sortOrder: item.sortOrder,
                    id: item.id,
                }
            )
        )));
    }

    checkIsCaseCountyActive(
        county: string,
        applicationType: AssistanceApplicationType
    ): Observable<boolean> {
        if (applicationType === AssistanceApplicationType.UFF) {
            return of(true);
        }
        return this._assistanceApplicationClient.isCaseCountyActive(
            county,
            applicationType
        );
    }

    getClientAssistanceApplications(
        clientId: string,
        applicationType: AssistanceApplicationType
    ): Observable<AssistanceApplication[]> {
        return this._assistanceApplicationClient.getClientAssistanceApplications(
            clientId,
            applicationType
        );
    }

    public loadDraft(draft: DraftApplication): void {

        this.currentDraftId = draft.id;
        const adults = [...draft.application.standardQuestions.householdInformation.adults];
        const minors = [...draft.application.standardQuestions.householdInformation.minors];
        draft.application.standardQuestions.householdInformation.adults = [];
        draft.application.standardQuestions.householdInformation.minors = [];

        this.form.patchValue(draft.application);

        const minorBirthYearValidators = Validators.birthYear({required: !this._isBirthYearAndSsnOptional, kind: 'minor'});
        const minorSsnValidators = this._isBirthYearAndSsnOptional
            ? [ssnValidator()]
            : [Validators.required, ssnValidator()];
        const adultBirthYearValidators = Validators.birthYear({required: !this._isBirthYearAndSsnOptional, kind: 'any'});
        const adultSsnValidators = this._isBirthYearAndSsnOptional
            ? [ssnValidator()]
            : [Validators.required, ssnValidator()];

        minors.forEach((x) => {
            this.minors.push(
                this.fb.group({
                    firstName: [x.firstName, Validators.required],
                    lastName: [x.lastName, Validators.required],
                    birthYear: new FormControl(
                        x.birthYear,
                        Validators.compose(minorBirthYearValidators)
                    ),
                    ssn: ['', minorSsnValidators]
                })
            );
        });
        adults.forEach((x) => {
            this.adults.push(
                this.fb.group({
                    firstName: [x.firstName, Validators.required],
                    lastName: [x.lastName, Validators.required],
                    birthYear: new FormControl(
                        x.birthYear,
                        Validators.compose(adultBirthYearValidators)
                    ),
                    ssn: ['', adultSsnValidators],
                    employmentStatus: [x.employmentStatus, Validators.required],
                    totalMonthlyIncome: [x.totalMonthlyIncome, Validators.required]
                })
            );
        });

        this.handleFormUpdates();

        this.router
            .navigate(['apply', this.currentPledgeType, 'application'], {
                queryParams: {id: draft.id}
            })
            .then();
    }

    public startNewApplication(): void {
        this.currentDraftId = null;
        this.handleFormUpdates();
        this.router
            .navigate(['apply', this.currentPledgeType, 'introduction'])
            .then();
    }

    public saveDraftApplication(): void {
        this.isSavingDraftSubject.next(true);
        const request = DraftApplicationModel.fromJS(this.form.getRawValue());
        request.eligibilityScreening.householdIncome = this.isNumeric(
            request.eligibilityScreening.householdIncome
        )
            ? request.eligibilityScreening.householdIncome
            : 0;
        request.standardQuestions.applicantInformation.totalHouseholdMonthlyIncome = this.isNumeric(
            request.standardQuestions.applicantInformation.totalHouseholdMonthlyIncome
        )
            ? request.standardQuestions.applicantInformation.totalHouseholdMonthlyIncome
            : 0;
        request.standardQuestions.applicantInformation.totalMonthlyIncome = this.isNumeric(
            request.standardQuestions.applicantInformation.totalMonthlyIncome
        )
            ? request.standardQuestions.applicantInformation.totalMonthlyIncome
            : 0;
        request.standardQuestions.householdInformation.adults.forEach((x) => {
            x.totalMonthlyIncome = this.isNumeric(x.totalMonthlyIncome)
                ? x.totalMonthlyIncome
                : 0;
        });
        request.standardQuestions.applicantInformation.totalAmountDue =
            this.isNumeric(request.standardQuestions.applicantInformation.totalAmountDue)
                ? request.standardQuestions.applicantInformation.totalAmountDue
                : 0;
        const assistanceRequestDescription = this.form.get('standardQuestions').get('assistanceRequestDescription') as FormGroup;
        Object.keys(assistanceRequestDescription.controls).forEach(key => {
            request.standardQuestions.assistanceRequestDescription[key] = assistanceRequestDescription.get(key).value ?? false;
        });

        request.isTranslated = this.translate.currentLang !== 'en';
        request.applicationType = this.programType;

        this.publicClient.saveDraft(request, this.currentDraftId, this.currentPledgeType).subscribe({
            next: response => {
                    if (!this.currentDraftId) {
                        this.currentDraftId = response;
                        const params = {id: response};
                        this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: params,
                            queryParamsHandling: 'merge'
                        }).then();
                        // First save: show the "saved & emailed" modal. Do NOT also fire a
                        // NotificationService toast here — toasts are SweetAlert2 popups too,
                        // and SweetAlert2 only allows one active popup, so a toast would
                        // immediately close this modal.
                        swal.fire({
                            heightAuto: false,
                            icon: 'success',
                            title: this.translate.instant('saveDraftModal.savedTitle'),
                            text: this.translate.instant('saveDraftModal.savedMessage'),
                        }).then();
                    } else {
                        this.injector.get(NotificationService).showSuccess(this.translate.instant('saveDraftModal.successMessage'));
                    }
                },
                error: (err) => {
                    console.error(err);
                this.injector.get(NotificationService).showError(this.translate.instant('saveDraftModal.errorMessage'));
                }
            })
            .add(() => this.isSavingDraftSubject.next(false));
    }

    private isNumeric(str: any): boolean {
        if (!str || str.toString().trim() === '') {
            return false;
        }
        return !isNaN(+str);
    }

    getAssistanceApplicationsPaged(
        applicationType: AssistanceApplicationType,
        searchValue: string,
        agencyId: string,
        managingAgencyId: string,
        status: AssistanceApplicationStatus,
        publicInternal: PublicInternalFilter,
        fundTypeIds: string[] = [],
        sortName = '',
        limit = 100,
        offset = 0,
        descending = false
    ): Observable<PagedListOfAssistanceApplicationListPreview> {
        const request = this._assistanceApplicationClient
            .getAssistanceApplicationsPaged(
                applicationType,
                searchValue,
                agencyId,
                managingAgencyId,
                status,
                publicInternal,
                fundTypeIds,
                limit,
                offset,
                sortName,
                null,
                descending
            )
            .pipe(share());
        // 003-retire-ngrx (US2): the assistanceApplication slice went
        // component-local — the list pages own their paged results now, so the
        // service just returns the shared request (no store dispatch).
        request.subscribe({
            error: () => {
                this._notificationService.showError(
                    'There was a problem retrieving the Assistance Applications'
                );
            }
        });
        return request;
    }

    exportAssistanceApplications(
        applicationType: AssistanceApplicationType,
        searchValue: string,
        agencyId: string,
        managingAgencyId: string,
        status: AssistanceApplicationStatus,
        publicInternal: PublicInternalFilter,
        fundTypeIds: string[] = [],
        sortName = '',
        descending = false
    ): Observable<FileResponse> {
        const request = this._assistanceApplicationClient
            .getAssistanceApplicationsExport(
                applicationType,
                searchValue,
                agencyId,
                managingAgencyId,
                status,
                publicInternal,
                fundTypeIds,
                sortName,
                null,
                descending
            )
            .pipe(share());

        return request;
    }

    public getProgramDefaultPayee(
        applicationType: AssistanceApplicationType
    ): Observable<Company> {
        const request =
            this._assistanceApplicationClient.getProgramDefaultPayee(
                applicationType
            );
        request.subscribe({
            error: () => {
                this._notificationService.showError(
                    'There was a problem retrieving the default program payee.'
                );
            }
        });
        return request;
    }

    public handleFormUpdates(): void {
        this.handleEligibilityFormUpdates();
        this.handleStandardQuestionFormUpdates();
    }

    private handleEligibilityFormUpdates(): void {
        this.handleEligibilityStateUpdates();
        this.handleEligibilityCountyUpdates();
        this.handleEligibilityHouseholdIncomeUpdates();
    }

    private handleStandardQuestionFormUpdates(): void {
        this.handleStandardQuestionsStateUpdates();
        this.handleStandardQuestionsCountyUpdates();
        this.handleStandardQuestionsTotalHouseholdMonthlyIncomeUpdates();
        this.handleStandardQuestionsAdultsUpdates();
        this.handleStandardQuestionsMinorsUpdates();
    }

    private handleEligibilityStateUpdates(): void {
        this.eligibilityState.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((state: string) => {
                if (state !== 'Other') {
                    this.standardQuestionsState.setValue(state);
                }
            });
    }

    private handleEligibilityCountyUpdates(): void {
        this.eligibilityCounty.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((county: string) => {
                this.standardQuestionsCounty.setValue(county);
            });
    }

    private handleStandardQuestionsStateUpdates(): void {
        this.standardQuestionsState.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((state: string) => {
                if (state === 'MO' || state === 'IL') {
                    this.eligibilityState.setValue(state);
                } else {
                    this.eligibilityState.setValue('Other');
                }
            });
    }

    private handleStandardQuestionsCountyUpdates(): void {
        this.standardQuestionsCounty.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((county: string) => {
                this.eligibilityCounty.setValue(county);
            });
    }

    private handleEligibilityHouseholdIncomeUpdates(): void {
        this.eligibilityHouseholdIncome.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((amount: string) => {
                this.standardQuestionsTotalHouseholdMonthlyIncome.setValue(
                    amount
                );
            });
    }

    private handleStandardQuestionsTotalHouseholdMonthlyIncomeUpdates(): void {
        this.standardQuestionsTotalHouseholdMonthlyIncome.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((amount: string) => {
                this.eligibilityHouseholdIncome.setValue(amount);
            });
    }

    private handleStandardQuestionsAdultsUpdates(): void {
        this.adults.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((adultsFormArray: FormArray) => {
                if (this.programType == AssistanceApplicationType.STLWater) {
                    this.eligibilityAdultCount.setValue(adultsFormArray?.length + 1);
                } else {
                    this.eligibilityAdultCount.setValue(adultsFormArray?.length);
                }
            });
    }

    private handleStandardQuestionsMinorsUpdates(): void {
        this.minors.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.$destroyed))
            .subscribe((minorsFormArray: FormArray) => {
                this.eligibilityChildrenCount.setValue(minorsFormArray?.length);
            });
    }

    public hasAccess(assistanceApplicationType: AssistanceApplicationType, user: User): boolean {
        if (!user?.programAccess?.length || environment.appConfig?.disableApplicationsFeature) {
            return false;
        }

        // Handle both enum numbers and string values in programAccess
        const enumName = AssistanceApplicationType[assistanceApplicationType];
        return user.programAccess.some(access =>
            access === assistanceApplicationType ||
            (access as any) === enumName ||
            access.toString() === assistanceApplicationType.toString()
        );
    }
}
